import * as Common from '@piccolohealth/pbs-common';
import { ChakraV3, Spin } from '@piccolohealth/ui';
import { dateTimeSchema, P, stringToNull, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useParticipantQuery } from '../../graphql/hooks/useParticipantQuery';
import { useUpdateParticipantMutation } from '../../graphql/hooks/useUpdateParticipantMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { ParticipantForm } from './ParticipantForm';
import { ParticipantVersionStatus } from './ParticipantVersionStatus';

type FormValues = Common.UpdateParticipantRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  firstName: z.string(),
  lastName: z.string(),
  dob: dateTimeSchema,
  gender: z.enum(Common.Gender),
  address: z.string().nullish(),
  postcode: z.string().nullish(),
  suburb: z.string().nullish(),
  state: z.string().nullish(),
  locationId: z.string().nullish(),
  labelIds: z.array(z.string()),
  status: z.enum(Common.ParticipantStatus),
  ndisNumber: z.string().nullish(),
  email: z.email().nullish(),
  phone: z.string().nullish(),
  picture: z.string().nullish(),
  keyContacts: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      phone: stringToNull(z.string().nullish()),
      email: stringToNull(z.email().nullish()),
      description: stringToNull(z.string().nullish()),
      labelIds: z.array(z.string()),
    }),
  ),
  fieldGroups: z.array(
    z.object({
      id: z.string(),
      fields: z.array(
        z.object({
          id: z.string(),
          value: z.any(),
        }),
      ),
    }),
  ),
});

interface Props {
  participantId: string;
  isDisabled?: boolean;
  openToTabValue?: string;
}

export const ParticipantEditModal = createModal<Props>((props) => {
  const { modal, participantId, isDisabled, openToTabValue } = props;
  const { errorToast, successToast, organization } = useAppContext();

  const { hide, remove, visible } = modal;

  const { data, isLoading, error } = useParticipantQuery({
    organizationId: organization.id,
    participantId: props.participantId,
  });

  const participant = data?.organization?.participant as Common.Participant | undefined;

  const defaultValues: FormValues = React.useMemo(() => {
    return {
      firstName: participant?.firstName,
      lastName: participant?.lastName,
      dob: participant?.dob,
      gender: participant?.gender,
      address: participant?.address,
      postcode: participant?.postcode,
      suburb: participant?.suburb,
      state: participant?.state,
      locationId: participant?.location?.id,
      labelIds: participant?.labels?.map((label) => label.id) ?? [],
      status: participant?.status,
      ndisNumber: participant?.ndisNumber,
      email: participant?.email,
      phone: participant?.phone,
      picture: participant?.picture,
      fieldGroups: participant?.fieldGroups,
      keyContacts:
        participant?.keyContacts?.map((keyContact) => {
          const labelIds = keyContact.labels.map((label) => label.id) ?? [];
          return { ...keyContact, labelIds };
        }) ?? [],
    };
  }, [participant]);

  const methods = useForm<FormValues>({
    values: defaultValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const updateParticipantMutation = useUpdateParticipantMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const request = P.deepObjectDiff(defaultValues, values);

      await updateParticipantMutation
        .mutateAsync({
          organizationId: organization.id,
          participantId: participantId,
          request: P.deepTrim(request),
        })
        .then(() => {
          hide();
          successToast('Participant updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating participant: ${err.message}`);
        });
    },
    [
      updateParticipantMutation,
      organization.id,
      participantId,
      defaultValues,
      hide,
      successToast,
      errorToast,
    ],
  );

  const content = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (error) {
      return <PiccoloError error={error} />;
    }

    if (!participant) {
      return (
        <PiccoloError
          error={
            new Common.PiccoloError({
              type: 'ParticipantNotFound',
              message: 'Participant not found',
            })
          }
        />
      );
    }

    return <ParticipantForm isDisabled={isDisabled} openToTabValue={openToTabValue} />;
  });

  const participantVersionStatus = P.run(() => {
    if (P.isNil(participant)) {
      return null;
    }

    return (
      !participant.isOnLatestFields &&
      !isDisabled && <ParticipantVersionStatus participantId={participantId} />
    );
  });

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove} size='xl'>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit a participant'
                subtitle='Please complete the following to proceed'
              />
              {participantVersionStatus && (
                <ChakraV3.Stack px={6} pb={6}>
                  {participantVersionStatus}
                </ChakraV3.Stack>
              )}
              <ChakraV3.Dialog.Body>{content}</ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
